<template>
  <div class="flex flex-col gap-4">
    <div class="flex items-center justify-between gap-2">
      <TextDescription>
        Exports worden zo snel mogelijk klaargezet. Meestal is dit binnen een
        paar minuten, soms duurt het iets langer. Exports blijven 7 dagen
        beschikbaar.
      </TextDescription>
      <BaseButton
        color="primary"
        outline
        small
        :loading="exportsStore.loading"
        @click="refreshExports"
      >
        <BaseIcon name="refresh" class="mr-1" />
        Vernieuwen
      </BaseButton>
    </div>

    <BaseCard class="overflow-x-auto">
      <LoadingWrapper
        :loading="exportsStore.loading"
        :error="exportsStore.loadingFailed"
      >
        <MessageComponent v-if="!rows.length" in-card color="info">
          Er zijn geen exportaanvragen.
        </MessageComponent>
        <BaseTable v-else :columns="columns" :rows="rows">
          <template #createdAt-cell="{ row }">
            <div class="flex items-center gap-2">
              <span>{{ row.createdAt }}</span>
              <BaseBadge
                class="mt-1 w-fit"
                :color="statusBadgeColor(row.rawStatus)"
              >
                {{ row.status }}
              </BaseBadge>
            </div>
          </template>
          <template #download-cell="{ row }">
            <BaseButton
              v-if="row.rawStatus === STATUS.DONE"
              color="primary"
              small
              :loading="loadingDownloadId === row.id"
              :disabled="
                loadingDownloadId !== null && loadingDownloadId !== row.id
              "
              @click="onExportDownload(row)"
            >
              <BaseIcon name="download-2" class="m-1" />
              Download
            </BaseButton>
            <span v-else>-</span>
          </template>
        </BaseTable>
      </LoadingWrapper>
    </BaseCard>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from "vue"
import { format, parse, addSeconds } from "date-fns"
import { triggerEvent } from "@/services/plausibleService.js"
import downloadUrl from "@/helpers/downloadUrl.js"
import toDate from "@/helpers/toDate.js"
import MessageComponent from "@/components/information/MessageComponent.vue"
import useNotificationStore from "@/stores/notificationStore.js"
import {
  EXPORT_TYPES,
  default as useExportsStore,
} from "@/stores/repower/exportsStore.js"
import useMeteringPointsStore from "@/stores/repower/meteringPointsStore"
import {
  TextDescription,
  BaseCard,
  BaseTable,
  BaseButton,
  BaseIcon,
  BaseBadge,
  LoadingWrapper,
} from "@repowerednl/ui-component-library"

const STATUS = {
  PENDING: "pending",
  PROCESSING: "processing",
  DONE: "done",
  FAILED: "failed",
}

const notificationStore = useNotificationStore()
const exportsStore = useExportsStore()
const meteringPointsStore = useMeteringPointsStore()
const loadingDownloadId = ref(null)

const columns = {
  createdAt: {
    label: "Aangemaakt op",
    classes: "tabular-nums",
  },
  meteringPoint: {
    label: "Meetpunt",
  },
  exportType: {
    label: "Type",
  },
  dateRange: {
    label: "Periode",
    classes: "tabular-nums",
  },
  frequency: {
    label: "Frequentie",
  },
  download: {
    label: "Download",
    classes: "!py-0",
  },
}

const statusLabelMap = {
  [STATUS.PENDING]: "Wachtrij",
  [STATUS.PROCESSING]: "Verwerken",
  [STATUS.DONE]: "Klaar",
  [STATUS.FAILED]: "Mislukt",
}

const exportTypeLabelMap = {
  [EXPORT_TYPES.MEASUREMENTS]: "Meetgegevens",
  [EXPORT_TYPES.RECORDS]: "Resultaten",
  [EXPORT_TYPES.RECORD_STREAMS]: "Resultaten",
}

const frequencyLabelMap = {
  "1min": "Per minuut",
  "15min": "Per kwartier",
  "1h": "Per uur",
  "1D": "Per dag",
}

const rows = computed(() => {
  return exportsStore.exports.map(normalizeRow).sort(sortByCreatedAtDesc)
})

function normalizeRow(item) {
  const dateStart = toDate(item.date_start)
  const dateEnd = toDate(item.date_end)
  const createdAt = toDate(item.created_at ?? item.created)
  const meteringPoint = meteringPointsStore.findMeteringPoint(
    item.metering_point_id,
  )?.displayName

  return {
    _id: "cell",
    id: item.id ?? item.uid,
    createdAtValue: createdAt?.getTime() ?? 0,
    createdAt: createdAt ? format(createdAt, "dd-MM-yyyy HH:mm") : "-",
    meteringPoint: meteringPoint ?? "-",
    exportType: exportTypeLabelMap[item.export_type] ?? "Onbekend",
    dateRange:
      dateStart && dateEnd
        ? `${format(dateStart, "dd-MM-yyyy")} - ${format(dateEnd, "dd-MM-yyyy")}`
        : "-",
    frequency: frequencyLabelMap[item.frequency] ?? "-",
    status: statusLabelMap[item.status] ?? "-",
    rawStatus: item.status,
  }
}

function sortByCreatedAtDesc(a, b) {
  return b.createdAtValue - a.createdAtValue
}

function statusBadgeColor(status) {
  switch (status) {
    case STATUS.DONE:
      return "success"
    case STATUS.FAILED:
      return "danger"
    default:
      return "info"
  }
}

async function refreshExports() {
  try {
    await exportsStore.loadExports()
  } catch (error) {
    notificationStore.pushError(
      "Fout bij het ophalen van exports",
      `De exportlijst kon niet worden opgehaald. Probeer het later opnieuw. (code: ${error?.code ?? "unknown"})`,
      "load-exports-error",
    )
  }
}

function verifyPresignedUrl(url) {
  const urlObj = new URL(url)
  const urlDate = urlObj.searchParams.get("X-Amz-Date")
  const urlExpires = urlObj.searchParams.get("X-Amz-Expires")

  if (urlDate && urlExpires) {
    const createdAt = parse(urlDate, "yyyyMMdd'T'HHmmssX", new Date())
    const expiresAt = addSeconds(createdAt, parseInt(urlExpires, 10))

    if (Date.now() >= expiresAt.getTime()) {
      return false
    }
  }
  return true
}

function onInvalidUrl() {
  notificationStore.pushError(
    "Ongeldige downloadlink",
    "De downloadlink voor deze export is ongeldig. Probeer het later opnieuw.",
    "download-export-error",
  )
}

function onDownloadError() {
  notificationStore.pushError(
    "Download mislukt",
    "Er is een fout opgetreden tijdens het downloaden van de export. Probeer het later opnieuw.",
    "download-export-error",
  )
}

async function onExportDownload(row) {
  triggerEvent(`export:download`)

  const exportId = row.id
  if (!exportId) {
    onDownloadError()
    return
  }

  loadingDownloadId.value = exportId
  try {
    const { url } = await exportsStore.getExportDownload(exportId)
    if (!url) {
      onDownloadError()
      return
    }

    if (verifyPresignedUrl(url)) {
      downloadUrl(url)
    } else {
      onInvalidUrl()
    }
  } catch {
    onDownloadError()
  } finally {
    loadingDownloadId.value = null
  }
}

onMounted(() => {
  refreshExports()
})
</script>
